import { openDownloadUrl } from "util/openDownloadUrl";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import ConditionalToolTip from "components/design/conditionalToolTip";
import {
  DnDArea,
  File,
  UploadLabel,
} from "components/design/UploadFileSection";
import {
  BulkProductReviewRow,
  BulkProductsReviewTable,
} from "components/MyProducts/BulkProductsReviewTable";
import { FormikForm } from "components/FormikForm/FormikForm";
import { DownloadIcon } from "components/icons/Download";
import { bulkProductImportSchema } from "formValidations/productAndServiceSchema";
import { useAbortController } from "hooks/useAbortController";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import { useState } from "react";
import {
  useConfirmBulkProductsMutation,
  useGetProductCategoriesQuery,
  useLazyPreviewBulkProductsQuery,
  useUploadBulkProductsMutation,
} from "store/apis/productAndServices";
import { PRODUCTS_IMPORT_SAMPLE_CSV_URL } from "types/contants/sampleExcelSheet";
import {
  ConfirmBulkProductRow,
  PreviewBulkProductsResult,
} from "types/Models/productAndServices";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { pluralize } from "utils/pluralize";

const ACCEPTED_FILE_TYPES = {
  "text/csv": [".csv"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

const BREADCRUMBS = [
  { name: "Upload file", step: 1 },
  { name: "Review products", step: 2 },
];

const getErrorMessage = (error: unknown) =>
  (error as BackendError)?.data?.error?.message;

const toReviewRows = (
  preview: PreviewBulkProductsResult
): BulkProductReviewRow[] =>
  preview.rows.map((row) => ({
    id: randomBytes(10).toString("hex"),
    row: row.row,
    product_name: row.product_name,
    price: Number(row.price) || 0,
    type: row.type,
    category: row.category.id || row.category.name || "",
    transaction_category: "",
  }));

const UploadStep = ({
  groupId,
  entityId,
  onSuccess,
}: {
  groupId: string;
  entityId: string;
  onSuccess: (rows: BulkProductReviewRow[]) => void;
}) => {
  const { alertToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, uploadState] = useUploadBulkProductsMutation();
  const [previewFile, previewState] = useLazyPreviewBulkProductsQuery();

  const uploadController = useAbortController({
    onAbort: () => alertToast({ message: "Upload cancelled" }),
  });

  const onDrop = async (files: File[]) => {
    if (!files.length) return;
    try {
      setFile(files[0]);
      await uploadFile({
        groupId,
        entityId,
        file: files[0],
        signal: uploadController.signal,
      }).unwrap();
    } catch (error) {
      setFile(null);
      if ((error as { status: string }).status === "FETCH_ERROR") return;
      alertToast({ message: getErrorMessage(error) }, error as Error);
    }
  };

  const onNext = async () => {
    const bulkFileId = uploadState.data?.bulk_file_id;
    if (!bulkFileId) return;
    try {
      const preview = await previewFile({
        groupId,
        entityId,
        bulkFileId,
      }).unwrap();

      onSuccess(toReviewRows(preview));
    } catch (error) {
      alertToast({ message: getErrorMessage(error) }, error as Error);
    }
  };

  return (
    <>
      <Modal.Body className="t-flex t-flex-col t-gap-3">
        <UploadLabel>
          Download the pre-formatted template to ensure your data is properly
          structured. Add the product details to the template and upload it.
        </UploadLabel>
        <span>
          <Button
            size="small"
            type="button"
            onClick={() => openDownloadUrl(PRODUCTS_IMPORT_SAMPLE_CSV_URL)}
          >
            <DownloadIcon />
            <span className="t-ml-1.5">Download Excel template</span>
          </Button>
        </span>
        {file ? (
          <File
            file={file}
            onCancel={uploadController.abort}
            onDelete={() => setFile(null)}
            isUploading={uploadState.isLoading}
            isUploaded={uploadState.isSuccess}
          />
        ) : (
          <DnDArea
            onDrop={onDrop}
            multiple={false}
            accept={ACCEPTED_FILE_TYPES}
            subText="File Format: csv/xls/xlsx"
          />
        )}
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button disabled={previewState.isFetching}>Cancel</Button>
        </Modal.RawClose>
        <Button
          disabled={!uploadState.data?.bulk_file_id || previewState.isFetching}
          isLoading={previewState.isFetching}
          customType="primary"
          onClick={onNext}
        >
          Next
        </Button>
      </Modal.FooterButtonGroup>
    </>
  );
};

const ReviewStep = ({
  groupId,
  entityId,
  rows,
  onDone,
}: {
  groupId: string;
  entityId: string;
  rows: BulkProductReviewRow[];
  onDone: () => void;
}) => {
  const { alertToast, successToast } = useToast();
  const { data: categories = [] } = useGetProductCategoriesQuery();
  const [confirmProducts] = useConfirmBulkProductsMutation();

  const onSubmit = async (values: { products: BulkProductReviewRow[] }) => {
    const products: ConfirmBulkProductRow[] = values.products.map((product) => {
      const matchedCategory = categories.find(
        (category) => category.uuid === product.category
      );

      return {
        row: product.row,
        product_name: product.product_name,
        price: product.price > 0 ? String(product.price) : null,
        type: product.type,
        ...(matchedCategory
          ? { category_id: matchedCategory.uuid }
          : product.category
          ? { category_name: product.category }
          : {}),
        ...(product.transaction_category
          ? { transaction_category_id: product.transaction_category }
          : {}),
      };
    });

    try {
      const result = await confirmProducts({
        groupId,
        entityId,
        products,
      }).unwrap();
      successToast({
        message: pluralize(
          result.products_added_count,
          "product added",
          "products added"
        ),
      });
      onDone();
    } catch (error) {
      alertToast({ message: getErrorMessage(error) }, error as Error);
    }
  };

  return (
    <FormikForm
      initialValues={{ products: rows }}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnChange
      validationSchema={bulkProductImportSchema}
    >
      {({ submitForm, isSubmitting, isValid }) => (
        <>
          <Modal.Body className="t-relative">
            <BulkProductsReviewTable />
            {isSubmitting && (
              <div className="t-h-full t-w-full t-flex t-items-center t-justify-center t-absolute t-inset-0">
                <div className="t-h-full t-w-full t-absolute t-bg-white t-opacity-60" />
                <Loader />
              </div>
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button disabled={isSubmitting}>Cancel</Button>
            </Modal.RawClose>
            <ConditionalToolTip
              condition={
                !isValid && "Fill in all required fields for every row"
              }
            >
              <Button
                customType="primary"
                disabled={isSubmitting || !isValid}
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Add products
              </Button>
            </ConditionalToolTip>
          </Modal.FooterButtonGroup>
        </>
      )}
    </FormikForm>
  );
};

export const BulkProductImport = ({ isOpen, close }: ModalProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [reviewRows, setReviewRows] = useState<BulkProductReviewRow[]>([]);

  const onClose = () => {
    close();
    setCurrentStep(1);
    setReviewRows([]);
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose} modal={false}>
      <Modal.Content
        size={currentStep === 2 ? "xxxl" : "large"}
        useCustomOverlay
      >
        <Modal.Header>
          <div>
            <Modal.Title>Bulk import products</Modal.Title>
            <Modal.Subtitle>
              <Stepper size="xs" direction="horizontal">
                {BREADCRUMBS.map(({ name, step }) => (
                  <Stepper.Step
                    key={step}
                    step={step}
                    isActive={currentStep === step}
                    clickable={step === 1 && currentStep > step}
                    onClick={() => setCurrentStep(1)}
                  >
                    {name}
                  </Stepper.Step>
                ))}
              </Stepper>
            </Modal.Subtitle>
          </div>
          <Modal.Close />
        </Modal.Header>
        {currentStep === 1 && (
          <UploadStep
            groupId={groupId}
            entityId={entityId}
            onSuccess={(rows) => {
              setReviewRows(rows);
              setCurrentStep(2);
            }}
          />
        )}
        {currentStep === 2 && (
          <ReviewStep
            groupId={groupId}
            entityId={entityId}
            rows={reviewRows}
            onDone={onClose}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
